import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
} from '@mui/material';
import useLocalization from 'hooks/useLocalization';
import React from 'react';
import { store, useSelector } from 'store';
import { fetchUpdateUserConfig } from 'store/slices/userConfig';
import { ChatUserModel } from 'types/chat-users/chat-user';
import { ViewCondition, FilterGroup } from 'types/userConfig/userConfig';
import { generateUniqueId, safeParseJSON } from 'utils/helpers';
import Grid from '@mui/material/Grid2';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CreateCustomViewConditions from './CreateCustomViewConditions';
import CreateCustomViewFilterGroups from './CreateCustomViewFilterGroups';
import { useNavigate, useParams } from 'react-router';
import { migrateCustomView, isFilterGroup } from 'utils/migrate-custom-views';

interface CreateCustomViewProps {
  open: boolean;
  onClose: () => void;
  createOrEdit: 'create' | 'edit' | null;
}
export interface CreateCustomViewRefProps {
  setData: (id: string | number | null, name: string, conditions: any[]) => void;
  clearData: () => void;
}
export type ViewFilterOptions = {
  key: keyof ChatUserModel | string; // מאפשר גם שדות שלא קיימים ב-ChatUserModel (כמו IsOpenUpdateTimeFrom)
  label: string | null;
  type: 'input' | 'select' | 'dateTime' | 'checkbox';
  options?: Array<{ value: any; label: string | null }>;
  multiple?: boolean;
  operators?: Array<'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'startswith' | 'endswith' | 'in' | 'notin' | 'isnull' | 'isnotnull'>;
  category?: 'contact' | 'lastSession' | 'lastMessageObject' | 'lastIncomingMessage' | 'lastOutgoingMessage'; // קטגוריה
  categoryLabel?: string; // שם הקטגוריה בעברית (למשל "איש קשר", "שיחה", "שיחה אחרונה")
};

const CreateCustomView = React.forwardRef<CreateCustomViewRefProps, CreateCustomViewProps>(
  ({ open, onClose, createOrEdit }, ref) => {
    //* localization
    const { currentLocale } = useLocalization();
    const localization = React.useMemo(
      () => currentLocale.settingsPopup.navigation.filters.create,
      [currentLocale],
    );

    //* Assignees
    const applicationUsers = useSelector((state) => state.applicationUsers);
    const selectableAssignees = React.useMemo(() => {
      return applicationUsers
        ?.filter((user) => {
          const userProfileConfig = safeParseJSON(user.ProfileConfig);

          const visible =
            userProfileConfig?.funnerInfo?.visibleInList !== undefined &&
            userProfileConfig?.funnerInfo?.visibleInList !== null
              ? userProfileConfig.funnerInfo.visibleInList
              : user.IsActive;

          return visible;
        })
        .filter((user) => user.FullName !== null && user.FullName !== undefined);
    }, [applicationUsers]);

    //* labels
    const chatUsersLabelsList = useSelector((state) => state.labels);

    const filterOptions: Array<ViewFilterOptions> = React.useMemo(
      () => [
        // ========== קטגוריה: איש קשר ==========
        {
          key: 'id',
          label: 'מזהה',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'phone',
          label: 'טלפון',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'name',
          label: 'שם',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'firstName',
          label: 'שם פרטי',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'lastName',
          label: 'שם משפחה',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'countryCallingCode',
          label: 'קוד מדינה',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'internationalPhoneFormat',
          label: 'פורמט טלפון בינלאומי',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'email',
          label: 'אימייל',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'gender',
          label: 'מגדר',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'pic',
          label: 'תמונה',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'profilePic',
          label: 'תמונת פרופיל',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'country',
          label: 'מדינה',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'consentStatus',
          label: 'סטטוס הסכמה',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'userId',
          label: 'מזהה משתמש',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'utmSource',
          label: 'מקור UTM',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'utmMedium',
          label: 'מדיום UTM',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'utmCampaign',
          label: 'קמפיין UTM',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'utmTerm',
          label: 'מונח UTM',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'utmContent',
          label: 'תוכן UTM',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'fireberryId',
          label: 'מזהה Fireberry',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'priorityId',
          label: 'מזהה עדיפות',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'externalSysId',
          label: 'מזהה מערכת חיצונית',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'creationTrigger',
          label: 'טריגר יצירה',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'requestSource',
          label: 'מקור בקשה',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'isWhatsappWindowOpen',
          label: 'חלון וואטסאפ פתוח',
          type: 'select',
          category: 'contact',
          categoryLabel: 'איש קשר',
          options: [
            { value: true, label: 'כן' },
            { value: false, label: 'לא' },
          ],
        },
        {
          key: 'activeBot',
          label: 'בוט פעיל',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        // ========== קטגוריה: שיחה אחרונה (lastSession) ==========
        {
          key: 'lastSession.phoneNumber',
          label: 'מספר טלפון',
          type: 'input',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
        },
        {
          key: 'lastSession.status',
          label: 'סטטוס',
          type: 'select',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
          options: [
            { value: 'open', label: 'פתוחה' },
            { value: 'closed', label: 'סגורה' },
          ],
        },
        {
          key: 'lastSession.dateStart',
          label: 'תאריך התחלה',
          type: 'dateTime',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
        },
        {
          key: 'lastSession.dateEnd',
          label: 'תאריך סיום',
          type: 'dateTime',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
        },
        {
          key: 'lastSession.durationMinutes',
          label: 'משך בדקות',
          type: 'input',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
        },
        {
          key: 'lastSession.unreadCount',
          label: 'מספר הודעות שלא נקראו',
          type: 'input',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
        },
        {
          key: 'lastSession.label',
          label: 'תגית',
          type: 'select',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
          options:
            chatUsersLabelsList?.map((label) => ({
              value: JSON.stringify(label),
              label: label.name,
            })) || [],
        },
        {
          key: 'lastSession.manager.id',
          label: localization.options.assignee.label,
          type: 'select',
          multiple: true,
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
          options: selectableAssignees.map((user) => ({
            value: user.Udid,
            label: user.FullName,
          })),
        },
        // ========== קטגוריה: הודעה אחרונה (lastMessageObject) ==========
        {
          key: 'lastMessageObject.id',
          label: 'מזהה',
          type: 'input',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
        },
        {
          key: 'lastMessageObject.dateCreated',
          label: 'תאריך יצירה',
          type: 'dateTime',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
        },
        {
          key: 'lastMessageObject.phone',
          label: 'טלפון',
          type: 'input',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
        },
        {
          key: 'lastMessageObject.status',
          label: 'סטטוס',
          type: 'select',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
          options: [
            { value: 'received', label: 'התקבלה' },
            { value: 'read', label: 'נקראה' },
            { value: 'delivered', label: 'נמסרה' },
            { value: 'sent', label: 'נשלחה' },
          ],
        },
        {
          key: 'lastMessageObject.type',
          label: 'סוג',
          type: 'select',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
          options: [
            { value: 'text', label: 'טקסט' },
            { value: 'image', label: 'תמונה' },
            { value: 'video', label: 'וידאו' },
            { value: 'audio', label: 'אודיו' },
            { value: 'document', label: 'מסמך' },
          ],
        },
        {
          key: 'lastMessageObject.direction',
          label: 'כיוון',
          type: 'select',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
          options: [
            { value: 'incoming', label: 'נכנסת' },
            { value: 'outgoing', label: 'יוצאת' },
          ],
        },
        {
          key: 'lastMessageObject.message',
          label: 'הודעה',
          type: 'input',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
        },
        {
          key: 'lastMessageObject.fileUrl',
          label: 'קישור לקובץ',
          type: 'input',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
        },
        // ========== קטגוריה: הודעה נכנסת אחרונה (lastIncomingMessage) ==========
        {
          key: 'lastIncomingMessage.id',
          label: 'מזהה',
          type: 'input',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
        },
        {
          key: 'lastIncomingMessage.dateCreated',
          label: 'תאריך יצירה',
          type: 'dateTime',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
        },
        {
          key: 'lastIncomingMessage.phone',
          label: 'טלפון',
          type: 'input',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
        },
        {
          key: 'lastIncomingMessage.status',
          label: 'סטטוס',
          type: 'select',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
          options: [
            { value: 'received', label: 'התקבלה' },
            { value: 'read', label: 'נקראה' },
          ],
        },
        {
          key: 'lastIncomingMessage.type',
          label: 'סוג',
          type: 'select',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
          options: [
            { value: 'text', label: 'טקסט' },
            { value: 'image', label: 'תמונה' },
            { value: 'video', label: 'וידאו' },
            { value: 'audio', label: 'אודיו' },
            { value: 'document', label: 'מסמך' },
          ],
        },
        {
          key: 'lastIncomingMessage.direction',
          label: 'כיוון',
          type: 'select',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
          options: [
            { value: 'incoming', label: 'נכנסת' },
          ],
        },
        {
          key: 'lastIncomingMessage.message',
          label: 'הודעה',
          type: 'input',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
        },
        {
          key: 'lastIncomingMessage.fileUrl',
          label: 'קישור לקובץ',
          type: 'input',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
        },
        // ========== קטגוריה: הודעה יוצאת אחרונה (lastOutgoingMessage) ==========
        {
          key: 'lastOutgoingMessage.id',
          label: 'מזהה',
          type: 'input',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
        },
        {
          key: 'lastOutgoingMessage.dateCreated',
          label: 'תאריך יצירה',
          type: 'dateTime',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
        },
        {
          key: 'lastOutgoingMessage.phone',
          label: 'טלפון',
          type: 'input',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
        },
        {
          key: 'lastOutgoingMessage.status',
          label: 'סטטוס',
          type: 'select',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
          options: [
            { value: 'sent', label: 'נשלחה' },
            { value: 'delivered', label: 'נמסרה' },
            { value: 'read', label: 'נקראה' },
          ],
        },
        {
          key: 'lastOutgoingMessage.type',
          label: 'סוג',
          type: 'select',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
          options: [
            { value: 'text', label: 'טקסט' },
            { value: 'image', label: 'תמונה' },
            { value: 'video', label: 'וידאו' },
            { value: 'audio', label: 'אודיו' },
            { value: 'document', label: 'מסמך' },
          ],
        },
        {
          key: 'lastOutgoingMessage.direction',
          label: 'כיוון',
          type: 'select',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
          options: [
            { value: 'outgoing', label: 'יוצאת' },
          ],
        },
        {
          key: 'lastOutgoingMessage.message',
          label: 'הודעה',
          type: 'input',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
        },
        {
          key: 'lastOutgoingMessage.fileUrl',
          label: 'קישור לקובץ',
          type: 'input',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
        },
      ],
      [
        chatUsersLabelsList,
        localization.options.assignee.label,
        localization.options.lastStatus.label,
        localization.options.lastStatus.value.close,
        localization.options.lastStatus.value.open,
        localization.options.lastStatusTimeAndDate.label,
        selectableAssignees,
      ],
    );

    const { viewId } = useParams();
    const navigate = useNavigate();

    const [filtersId, setFiltersId] = React.useState<string | number | null>(null);
    const [filtersName, setFiltersName] = React.useState<string>('');
    const [filtersConditions, setFiltersConditions] = React.useState<ViewCondition[]>([]); // למבנה ישן
    const [filterGroups, setFilterGroups] = React.useState<FilterGroup[]>([]); // למבנה חדש
    const [filtersLogic, setFiltersLogic] = React.useState<'or' | 'and'>('and');
    const [useNewStructure, setUseNewStructure] = React.useState<boolean>(true); // דגל לשימוש במבנה החדש

    React.useImperativeHandle(ref, () => ({
      setData,
      clearData,
    }));

    const setData = React.useCallback(
      (id: string | number | null, name: string, conditions: any[]) => {
        setFiltersId(id);
        setFiltersName(name);
        
        // בדיקה אם זה מבנה חדש (FilterGroup[]) או ישן (ViewCondition[])
        if (conditions.length > 0 && isFilterGroup(conditions[0])) {
          setUseNewStructure(true);
          setFilterGroups(conditions as FilterGroup[]);
          setFiltersConditions([]);
        } else {
          // המרת מבנה ישן למבנה חדש
          const migrated = migrateCustomView({
            id: id || '',
            name,
            filters: conditions,
          });
          setUseNewStructure(true);
          setFilterGroups(migrated.filters as FilterGroup[]);
          setFiltersLogic(migrated.filtersLogic || 'and');
          setFiltersConditions([]);
        }
      },
      [],
    );
    const clearData = React.useCallback(() => {
      setFiltersId(null);
      setFiltersName('');
      setFiltersConditions([]);
      setFilterGroups([]);
      setFiltersLogic('and');
      setUseNewStructure(true);
    }, []);
    const handleClose = () => {
      onClose();
      setTimeout(() => {
        clearData();
      }, 300);
    };

    const handleFiltersName = (value: string) => {
      setFiltersName(value);
    };
    //* Conditions handlers
    const addNewCondition = () => {
      const newConditionObj = {
        id: generateUniqueId(
          filtersConditions.map((cond) => cond.id.toString()),
          5,
        ),
        key: '' as keyof ChatUserModel,
        value: null,
      };

      setFiltersConditions((prevList) => [...prevList, newConditionObj]);
    };
    const deleteCondition = (id: string | number) => {
      setFiltersConditions((prevList) => prevList.filter((cond) => cond.id !== id));
    };
    const handleConditionParams = (id: string | number, params: Partial<ViewCondition>) => {
      setFiltersConditions((prevList) => {
        const newList = [...prevList];
        const targetConditionIndex = newList.findIndex((cond) => cond.id === id);
        newList[targetConditionIndex] = {
          ...newList[targetConditionIndex],
          ...params,
        };
        return newList;
      });
    };

    const saveCurrentFilters = (id: string | number | null = null) => {
      handleClose();
      const storeState = store.getState();

      let newFiltersAll = [...storeState.userConfig.customViews];
      
      // שימוש במבנה החדש
      const filtersToSave = useNewStructure && filterGroups.length > 0 
        ? filterGroups 
        : filtersConditions; // fallback למבנה ישן

      if (createOrEdit === 'edit') {
        const currentFilters = newFiltersAll.find((filters) => filters.id === id);
        if (currentFilters) {
          const newFilters = {
            ...currentFilters,
            name: filtersName,
            filters: filtersToSave,
            ...(useNewStructure && filterGroups.length > 0 && { filtersLogic }),
          };

          newFiltersAll = newFiltersAll.map((filter) => {
            if (filter.id === currentFilters.id) {
              return newFilters;
            }
            return filter;
          });

          if (viewId === currentFilters.id.toString())
            navigate(`/chats/${storeState.chat.selectedChatUser?.UserDisplayPhoneNumber || ''}`);
        }
      } else {
        const newFilterObj = {
          name: filtersName,
          filters: filtersToSave,
          id: generateUniqueId(
            newFiltersAll.map((filter) => filter.id.toString()),
            5,
          ),
          ...(useNewStructure && filterGroups.length > 0 && { filtersLogic }),
        };

        newFiltersAll.push(newFilterObj);
      }

      fetchUpdateUserConfig(
        {
          customViews: newFiltersAll,
        },
        true,
      );
    };

    const buttonDisabled = React.useMemo(() => {
      if (filtersName === '') return true;
      if (useNewStructure) {
        // בדיקה שיש לפחות קבוצה אחת עם לפחות תנאי אחד תקין
        if (filterGroups.length === 0) return true;
        
        // בדיקה שיש לפחות קבוצה אחת עם לפחות תנאי אחד תקין
        const hasValidGroup = filterGroups.some((group) => {
          if (group.conditions.length === 0) return false;
          // בדיקה שכל התנאים תקינים (יש field, operator, ו-value אם נדרש)
          return group.conditions.some((cond) => {
            if (!cond.field || !cond.operator) return false;
            // עבור isnull/isnotnull לא צריך value
            if (cond.operator === 'isnull' || cond.operator === 'isnotnull') return true;
            // עבור שאר ה-operators צריך value
            return cond.value !== undefined && cond.value !== null && cond.value !== '';
          });
        });
        
        return !hasValidGroup;
      }
      return filtersConditions.length === 0;
    }, [filtersConditions.length, filterGroups, filtersName, useNewStructure]);

    return (
      <Dialog 
        onClose={handleClose} 
        open={open} 
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '900px',
          },
        }}
      >
        <DialogTitle>
          <Grid container spacing={2} alignItems={'center'}>
            <Grid size="grow">{localization.title}</Grid>
            <Grid size="auto">
              <IconButton onClick={handleClose}>
                <CloseOutlinedIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: '8px !important',
          }}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={localization.nameLabel}
                value={filtersName}
                onChange={(e) => handleFiltersName(e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <Divider />
            </Grid>
            <Grid size={12}>
              {useNewStructure ? (
                <CreateCustomViewFilterGroups
                  options={filterOptions}
                  filterGroups={filterGroups}
                  filtersLogic={filtersLogic}
                  onFilterGroupsChange={setFilterGroups}
                  onFiltersLogicChange={setFiltersLogic}
                />
              ) : (
                <CreateCustomViewConditions
                  options={filterOptions}
                  conditions={filtersConditions}
                  addNewCondition={addNewCondition}
                  deleteCondition={deleteCondition}
                  onChange={handleConditionParams}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleClose}>
            {localization.close}
          </Button>
          <Button variant="contained" onClick={() => saveCurrentFilters(filtersId)} disabled={buttonDisabled}>
            {localization.save}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default CreateCustomView;
